import type { CSSProperties, ReactNode } from 'react';
import { cn } from './cn';

export interface TooltipProps {
  /** Text shown on hover/focus. */
  content: string;
  /** Trigger element (usually an icon button or info icon). */
  children: ReactNode;
  className?: string;
  /** Optional inline style passed to the wrapper — needed when the tooltip
   *  trigger is sized by a parent flex container (e.g. ColorBar segments). */
  style?: CSSProperties;
}

/**
 * Lightweight CSS-only tooltip — Quiet Atlas tokens.
 * No Radix/Popper — intentionally minimal for sparse use ("why we ask this?"
 * info icons in the quiz, segment hovers on the result color bar).
 *
 * Appears on hover (group-hover) and keyboard focus (group-focus-within).
 * Non-blocking: `pointer-events-none` on the bubble.
 *
 * Wrapper uses `inline-block` (not inline-flex) so it doesn't break parent
 * flex-layout sizing — explicit width set via `style` is honored on the
 * wrapper directly.
 */
export function Tooltip({ content, children, className, style }: TooltipProps) {
  return (
    <span
      className={cn('group relative inline-block', className)}
      style={style}
    >
      {children}
      <span
        role="tooltip"
        className={cn(
          'pointer-events-none invisible absolute left-1/2 top-full z-50 mt-2 -translate-x-1/2',
          'whitespace-normal rounded-md bg-ink-900 px-2 py-1 text-xs text-paper shadow-lg',
          'max-w-xs opacity-0 transition-opacity duration-150',
          'group-hover:visible group-hover:opacity-100',
          'group-focus-within:visible group-focus-within:opacity-100',
        )}
      >
        {content}
      </span>
    </span>
  );
}
